import React, { ReactNode, useState } from "react"

import { gql, useClient } from "urql"
import { pipe, subscribe } from "wonka"

import { AlertLevel } from "~tailwindui/types/enums"

import { path } from "~Tutor/TutorRoutes"
import {
  AlertMessageBox,
  Button,
  DescriptionItem,
  StyledLink,
} from "~tailwindui/Basics"
import Panel from "~tailwindui/Basics/Panel"

import ordered from "src/ordered"
import titlecase from "src/titlecase"

import ConsistentTutorWarning from "components/ConsistentTutorWarning"
import LocalTime from "components/LocalTime"

type StudyGroupGeneration = {
  id: string
  startsOn: Date
  endsOn: Date
}

type Availability = {
  id: string
  startsAt: Date
  endsAt: Date
  weekday: string
  weekdayNumber: number
}

type StudyGroup = {
  id: string
  name: string
  formattedDetails: string
  minTutorConsistencyRate: number
  maxSubsEstimate: number
  foreignLanguage: {
    id: string
    name: string
  }
  subjects: {
    id: string
    name: string
  }[]
  studyGroupGeneration: StudyGroupGeneration
  availabilities: Availability
  organization: {
    id: string
    name: string
    details: string
    emailTimeZone: string
  }
}

export type InvitationCardProps = {
  studyGroup: StudyGroup
  invitation: {
    id: string
    organization: {
      id: string
      name: string
    }
  }
}

const InvitationCard: React.FC<InvitationCardProps> = ({
  invitation,
  studyGroup,
}) => {
  const [errors, setErrors] = useState([])
  const [status, setStatus] = useState<string | null>()
  const [success, setSuccess] = useState<ReactNode | null>()
  const [inFlight, setInFlight] = useState(false)
  const client = useClient()

  const register = () => {
    setInFlight(true)
    const { unsubscribe } = pipe(
      client.subscription(registerSubscription, {
        studyGroupId: studyGroup.id,
        studyGroupTutorInvitationId: invitation.id,
      }),
      subscribe(response => {
        const { results, studyGroup } =
          response.data.studyGroupInvitationRegister

        const status = results[0].status
        setErrors(results.filter(result => !result.success))

        if (status === "failure" || status === "success") {
          setInFlight(false)
          unsubscribe()
          setStatus(null)
        } else {
          setStatus(titlecase(status))
        }

        if (status === "success") {
          setSuccess(
            <>
              Registration successful!{" "}
              <StyledLink
                to={path("studyGroupDetails", {
                  id: studyGroup.id,
                })}
              >
                Click here to view your new group
              </StyledLink>
            </>
          )
        }
      })
    )
  }

  return (
    <Panel className="flex max-w-xl flex-col">
      <div>
        {(studyGroup.maxSubsEstimate || studyGroup.maxSubsEstimate === 0) && (
          <ConsistentTutorWarning
            className="mt-0 w-full rounded-t-lg rounded-b-none"
            minTutorConsistencyRate={studyGroup.minTutorConsistencyRate}
            maxSubsEstimate={studyGroup.maxSubsEstimate}
          />
        )}
      </div>

      <div className="flex flex-1 flex-col items-center justify-between space-y-3 py-4 px-6">
        <div className="text-center text-lg font-bold text-green-600">
          <span>
            {studyGroup.name}: {studyGroup.id}
          </span>
          <div className="font-medium text-orange-600">
            {studyGroup.foreignLanguage?.name}
          </div>
        </div>

        <div className="grow text-sm">
          <DescriptionItem
            label="Session Dates"
            value={
              <span>
                <LocalTime
                  timestamp={studyGroup.studyGroupGeneration.startsOn}
                  omitTime
                />{" "}
                &ndash;{" "}
                <LocalTime
                  timestamp={studyGroup.studyGroupGeneration.endsOn}
                  omitTime
                />
              </span>
            }
          />

          <DescriptionItem
            label="Session Times"
            value={
              <ul className="m-0">
                {ordered(studyGroup.availabilities, "weekdayNumber").map(
                  availability => (
                    <li key={availability.id}>
                      {availability.weekday}{" "}
                      <LocalTime
                        timestamp={availability.startsAt}
                        format="h:mm A"
                      />
                      <span> &ndash; </span>
                      <LocalTime
                        timestamp={availability.endsAt}
                        format="h:mm A (z)"
                      />
                    </li>
                  )
                )}
              </ul>
            }
          />

          <hr className="my-2" />

          <DescriptionItem label="Study Group" value={studyGroup.name} />
          {studyGroup.formattedDetails && (
            <DescriptionItem
              label="Study Group Details"
              value={
                <span
                  dangerouslySetInnerHTML={{
                    __html: studyGroup.formattedDetails,
                  }}
                />
              }
            />
          )}
          <DescriptionItem
            label="Subject"
            value={studyGroup.subjects.map(subject => subject.name).join(", ")}
          />

          {studyGroup.organization && (
            <>
              <DescriptionItem
                label="Organization"
                value={studyGroup.organization.name}
              />
              {studyGroup.organization.details && (
                <DescriptionItem
                  label="Organization Details"
                  value={studyGroup.organization.details}
                />
              )}
              <DescriptionItem
                label="Organization Time Zone"
                value={studyGroup.organization.emailTimeZone}
              />
            </>
          )}
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-center gap-4">
        {errors.length > 0 && (
          <AlertMessageBox>
            <div className="flex list-disc flex-col space-y-1 pl-5">
              {errors.map((error, index) => (
                <div key={index}>
                  {error.message}
                  {error.conflictingSessions && (
                    <ul>
                      {error.conflictingSessions.map(session => (
                        <li key={session.id}>
                          <StyledLink
                            to={path("sessionDetails", {
                              id: session.id,
                            })}
                            className="underline"
                            target="_blank"
                            rel="noreferrer noopener"
                          >
                            <LocalTime timestamp={session.startsAt} />
                          </StyledLink>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </AlertMessageBox>
        )}

        {status ? (
          <AlertMessageBox level={AlertLevel.Info}>{status}</AlertMessageBox>
        ) : success ? (
          <AlertMessageBox
            level={AlertLevel.Success}
            className="inline-block min-w-[20%]"
          >
            {success}
          </AlertMessageBox>
        ) : (
          <Button disabled={inFlight} onClick={register}>
            Register
          </Button>
        )}
      </div>
    </Panel>
  )
}

const registerSubscription = gql`
  subscription ($studyGroupId: ID!, $studyGroupTutorInvitationId: ID!) {
    studyGroupInvitationRegister(
      studyGroupId: $studyGroupId
      studyGroupTutorInvitationId: $studyGroupTutorInvitationId
    ) {
      results {
        success
        status
        message
        conflictingSessions {
          id
          startsAt
        }
      }
      studyGroup {
        id
        name
      }
    }
  }
`

export default InvitationCard
